import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../../environment';
import { ModalService } from '@common/services/ModalService/ModalService';
import { TokenEndpointResponse } from './tokenEndpointResponse.model';

@Injectable()
export class AuthService {
  accessToken: string;
  companyKey: string;
  loginStatus: string;
  callbacks = [];

  constructor(
    public http: HttpClient,
    public router: Router,
    public modalService: ModalService,
  ) {
    this.accessToken = localStorage.getItem('accessToken')
      ? localStorage.getItem('accessToken')
      : sessionStorage.getItem('accessToken');

    this.companyKey = localStorage.getItem('companyKey')
      ? localStorage.getItem('companyKey')
      : sessionStorage.getItem('companyKey');
  }

  getAccessToken(): string {
    return this.accessToken;
  }

  getCompanyKey(): string {
    return this.companyKey;
  }

  getLoginStatus(): string {
    return this.loginStatus;
  }

  login(username: string, password: string, rememberMe: boolean): void {
    if (rememberMe) {
      localStorage.setItem('rememberMe', 'true');
    }

    const {
      clientId,
      clientSecret,
    } = environment.zoniferoServicesAuth;

    const clientCredentials = `${clientId}:${clientSecret}`;

    const request = this.http.post(
      environment.zoniferoServicesAuth.accessTokenUrl,
      `grant_type=password&username=${username}&password=${password}`,
      {
        headers: new HttpHeaders()
          .set('Authorization', 'Basic ' + btoa(clientCredentials))
          .set('Content-Type', 'application/x-www-form-urlencoded'),
      },
    );

    this.handleTokenUrlRequest(request, 'password');
  }

  handleTokenUrlRequest(request, grantType: string) {
    request
      .subscribe(
        (res: TokenEndpointResponse) => {
          if (res.user_role === 'EMPLOYEE') {
            this.modalService.openModal({ type: 'accessDenied' });
            return;
          }

          if (localStorage.getItem('rememberMe')) {
            localStorage.setItem('accessToken', res.access_token);
            localStorage.setItem('refreshToken', res.refresh_token);
            localStorage.setItem('companyKey', res.company_key);
          } else {
            sessionStorage.setItem('accessToken', res.access_token);
            sessionStorage.setItem('refreshToken', res.refresh_token);
            sessionStorage.setItem('companyKey', res.company_key);
          }

          this.accessToken = res.access_token;
          this.companyKey = res.company_key;

          if (grantType === 'refreshToken') {
            this.callbacks.forEach(callbackToCall => callbackToCall());
            this.callbacks = [];
          } else {
            this.router.navigateByUrl('/dashboard');
          }
        },
        (err) => {
          if (err.status === 401) {
            if (grantType === 'password') {
              this.modalService.openModal({ type: 'incorrectLoginPassword' });
            }

            if (grantType === 'refreshToken') {
              this.logout();
              this.router.navigateByUrl('/auth/login');
              this.callbacks = [];
            }
          } else {
            this.modalService.openModal({ type: 'errorOccurred' });
          }
        },
      );
  }

  microsoftLogin(code: string): void {
    this.http.get<TokenEndpointResponse>(
      environment.microsoftAuth.ZSAuthUrl,
      {
        params: { code },
      },
    )
      .subscribe(
        (res: TokenEndpointResponse) => {
          if (res.user_role === 'EMPLOYEE') {
            this.router.navigateByUrl('/auth/login');
            this.modalService.openModal({ type: 'accessDenied' });
            return;
          }

          sessionStorage.setItem('accessToken', res.access_token);
          sessionStorage.setItem('refreshToken', res.refresh_token);
          sessionStorage.setItem('companyKey', res.company_key);
          this.accessToken = res.access_token;
          this.companyKey = res.company_key;
          this.router.navigateByUrl('/dashboard');
        },
        (err) => {
          this.loginStatus = 'Connection error';
        },
      );
  }

  logout(): void {
    this.accessToken = null;
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('companyKey');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('companyKey');
  }

  handleRequestError(error, callback) {
    if (error.status === 401) {
      this.refreshAccessToken(callback);
    }
  }

  refreshAccessToken(callback) {
    const {
      clientId,
      clientSecret,
    } = environment.zoniferoServicesAuth;

    const clientCredentials = `${clientId}:${clientSecret}`;
    const refreshToken = localStorage.getItem('refreshToken');

    this.callbacks.push(callback);

    if (refreshToken) {
      if (this.callbacks.length === 1) {
        const request = this.http.post(
          environment.zoniferoServicesAuth.accessTokenUrl,
          `grant_type=refresh_token&refresh_token=${refreshToken}`,
          {
            headers: new HttpHeaders()
              .set('Authorization', 'Basic ' + btoa(clientCredentials))
              .set('Content-Type', 'application/x-www-form-urlencoded'),
          },
        );

        this.handleTokenUrlRequest(request, 'refreshToken');
      }
    } else {
      this.logout();
      this.router.navigateByUrl('/auth/login');
    }
  }

  requestPasswordReset(email) {
    return this.http.post(
      environment.zoniferoServicesAuth.passwordResetUrl,
      { email },
    );
  }

  confirmPasswordReset(data) {
    return this.http.post(
      environment.zoniferoServicesAuth.confirmPasswordReset,
      data,
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/common/services/AuthService/AuthService.ts